/**
 * GWP Bundle Manager
 * Automatically manages Gift-With-Purchase items for qualifying device purchases
 *
 * Deesse PRO Shopify Theme
 * Version: 1.0.0
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================

  // GWP Configuration using VARIANT IDs (extracted from product data)
  const GWP_CONFIG = {
    // PRO by Deesse PRO (Product ID: 6762733043889)
    '6762733043889': {
      name: 'PRO',
      gwpVariants: [
        { variantId: '56340492583289', name: 'Luxury Signature Device Bag (Large)' }
      ]
    },
    // Sculpta by Deesse PRO (Product ID: 7593718710449)
    '7593718710449': {
      name: 'Sculpta',
      gwpVariants: [
        { variantId: '56340492943737', name: 'Luxury Signature Device Bag (Small)' }
      ]
    },
    // Express by Deesse PRO (Product ID: 7512196841649)
    '7512196841649': {
      name: 'Express',
      gwpVariants: [
        { variantId: '56340492943737', name: 'Luxury Signature Device Bag (Small)' }
      ]
    }
  };

  // All GWP variant IDs for quick lookup
  const ALL_GWP_VARIANT_IDS = new Set([
    '56340492583289', // Luxury Signature Device Bag (Large)
    '56340492943737'  // Luxury Signature Device Bag (Small)
  ]);

  // Qualifying product IDs
  const QUALIFYING_PRODUCT_IDS = new Set(Object.keys(GWP_CONFIG));

  // Debounce timer (ms)
  const DEBOUNCE_DELAY = 300;

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const state = {
    isSyncing: false,
    lastCartState: null,
    checkoutBlocked: false,
    retryCount: 0,
    maxRetries: 2
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function isGWPVariant(variantId) {
    return ALL_GWP_VARIANT_IDS.has(String(variantId));
  }

  function isQualifyingProduct(productId) {
    return QUALIFYING_PRODUCT_IDS.has(String(productId));
  }

  function getGWPConfigForProduct(productId) {
    return GWP_CONFIG[String(productId)] || null;
  }

  function log(message, data = null) {
    if (data) {
      console.log(`[GWP] ${message}`, data);
    } else {
      console.log(`[GWP] ${message}`);
    }
  }

  function warn(message, data = null) {
    if (data) {
      console.warn(`[GWP] ${message}`, data);
    } else {
      console.warn(`[GWP] ${message}`);
    }
  }

  // ============================================
  // CART API HELPERS
  // ============================================

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      warn('Error fetching cart:', error);
      return null;
    }
  }

  async function addToCart(items) {
    try {
      log('Attempting to add GWP items:', items);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      if (!response.ok) {
        const errorData = await response.json();
        warn('Shopify add error response:', errorData);
        throw new Error(errorData.description || errorData.message || 'Failed to add to cart');
      }
      return await response.json();
    } catch (error) {
      warn('Error adding to cart:', error);
      return null;
    }
  }

  async function updateCartItems(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return await response.json();
    } catch (error) {
      warn('Error updating cart:', error);
      return null;
    }
  }

  // ============================================
  // GWP CALCULATION LOGIC
  // ============================================

  /**
   * Calculate required GWP items based on qualifying devices in cart
   * @param {Array} cartItems - Array of cart line items
   * @returns {Map} variantId -> { quantity, sources }
   */
  function calculateRequiredGWPs(cartItems) {
    const required = new Map();

    cartItems.forEach(item => {
      const productId = String(item.product_id);

      if (isQualifyingProduct(productId)) {
        const config = getGWPConfigForProduct(productId);
        const deviceQty = item.quantity;

        config.gwpVariants.forEach(gwp => {
          const variantId = String(gwp.variantId);
          const existing = required.get(variantId) || { quantity: 0, sources: [], name: gwp.name };
          existing.quantity += deviceQty;
          if (!existing.sources.includes(config.name)) {
            existing.sources.push(config.name);
          }
          required.set(variantId, existing);
        });
      }
    });

    return required;
  }

  /**
   * Get current GWP items in cart
   * @param {Array} cartItems - Array of cart line items
   * @returns {Map} variantId -> { quantity, key }
   */
  function getCurrentGWPsInCart(cartItems) {
    const current = new Map();

    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);

      // Check if it's a GWP item by variant ID or _gwp property
      const hasGWPProperty = item.properties && item.properties._gwp === 'true';
      const isKnownGWP = isGWPVariant(variantId);

      if (isKnownGWP || hasGWPProperty) {
        current.set(variantId, {
          quantity: item.quantity,
          key: item.key,
          hasProperty: hasGWPProperty
        });
      }
    });

    return current;
  }

  /**
   * Calculate what needs to be added, updated, or removed
   * @param {Map} required - Required GWP items
   * @param {Map} current - Current GWP items in cart
   * @returns {Object} { toAdd, toUpdate, toRemove }
   */
  function calculateGWPDelta(required, current) {
    const toAdd = [];
    const toUpdate = {};
    const toRemove = {};

    // Check items that should exist
    required.forEach((reqData, variantId) => {
      const currData = current.get(variantId);

      if (!currData) {
        // Need to add this GWP
        toAdd.push({
          id: parseInt(variantId, 10),
          quantity: reqData.quantity,
          properties: { _gwp: 'true' }
        });
      } else if (currData.quantity !== reqData.quantity) {
        // Need to update quantity
        toUpdate[currData.key] = reqData.quantity;
      }
    });

    // Check items that should NOT exist (orphaned GWPs)
    current.forEach((currData, variantId) => {
      if (!required.has(variantId)) {
        toRemove[currData.key] = 0;
      }
    });

    return { toAdd, toUpdate, toRemove };
  }

  // ============================================
  // SYNC ORCHESTRATION
  // ============================================

  /**
   * Main sync function - orchestrates GWP bundle synchronization
   */
  async function syncGWPBundle() {
    if (state.isSyncing) {
      log('Sync already in progress, skipping...');
      return;
    }

    state.isSyncing = true;
    setCheckoutBlocked(true, 'Updating your free gifts...');
    announceToScreenReader('Updating your free gifts, please wait...');

    try {
      // Fetch current cart state
      const cart = await fetchCart();
      if (!cart) {
        throw new Error('Failed to fetch cart');
      }

      // Calculate what's needed
      const required = calculateRequiredGWPs(cart.items);
      const current = getCurrentGWPsInCart(cart.items);
      const delta = calculateGWPDelta(required, current);

      log('Sync Delta:', {
        toAdd: delta.toAdd.length,
        toUpdate: Object.keys(delta.toUpdate).length,
        toRemove: Object.keys(delta.toRemove).length
      });

      // Check if any changes are needed
      const hasChanges = delta.toAdd.length > 0 ||
                        Object.keys(delta.toUpdate).length > 0 ||
                        Object.keys(delta.toRemove).length > 0;

      if (!hasChanges) {
        log('Cart is already in sync');
        state.retryCount = 0;
        setCheckoutBlocked(false);
        return;
      }

      // Process removals and updates in one batch
      const batchUpdates = { ...delta.toUpdate, ...delta.toRemove };
      if (Object.keys(batchUpdates).length > 0) {
        const updateResult = await updateCartItems(batchUpdates);
        if (!updateResult) {
          throw new Error('Failed to update cart items');
        }
      }

      // Process additions
      if (delta.toAdd.length > 0) {
        const addResult = await addToCart(delta.toAdd);
        if (!addResult) {
          throw new Error('Failed to add GWP items');
        }
      }

      // Verify sync was successful
      const verifyCart = await fetchCart();
      if (!verifyCart) {
        throw new Error('Failed to verify cart after sync');
      }

      const verifyRequired = calculateRequiredGWPs(verifyCart.items);
      const verifyCurrent = getCurrentGWPsInCart(verifyCart.items);
      const isInSync = validateSync(verifyRequired, verifyCurrent);

      if (isInSync) {
        log('Sync complete - cart is in sync');
        state.retryCount = 0;
        setCheckoutBlocked(false);
        announceToScreenReader('Your free gifts have been updated');
        refreshCartUI();
      } else if (state.retryCount < state.maxRetries) {
        warn('Sync verification failed, retrying...');
        state.retryCount++;
        state.isSyncing = false;
        await syncGWPBundle();
        return;
      } else {
        warn('Sync failed after max retries');
        state.retryCount = 0;
        setCheckoutBlocked(false);
      }

    } catch (error) {
      warn('Sync error:', error);
      setCheckoutBlocked(false);
    } finally {
      state.isSyncing = false;
    }
  }

  /**
   * Validate that current cart matches required state
   */
  function validateSync(required, current) {
    // Check all required items exist with correct quantities
    for (const [variantId, reqData] of required) {
      const currData = current.get(variantId);
      if (!currData || currData.quantity !== reqData.quantity) {
        return false;
      }
    }

    // Check no orphaned GWPs exist
    for (const variantId of current.keys()) {
      if (!required.has(variantId)) {
        return false;
      }
    }

    return true;
  }

  // ============================================
  // UI UPDATES
  // ============================================

  /**
   * Block or unblock checkout buttons
   */
  function setCheckoutBlocked(blocked, message = '') {
    state.checkoutBlocked = blocked;

    const checkoutButtons = document.querySelectorAll(
      '#checkout, #CartDrawer-Checkout, [name="checkout"], .cart__checkout-button'
    );

    checkoutButtons.forEach(button => {
      if (blocked) {
        button.disabled = true;
        button.classList.add('gwp-syncing');
        if (!button.hasAttribute('data-original-text')) {
          button.setAttribute('data-original-text', button.textContent.trim());
        }
        button.innerHTML = `
          <span class="gwp-spinner" aria-hidden="true"></span>
          <span>${message || 'Updating...'}</span>
        `;
      } else {
        button.disabled = false;
        button.classList.remove('gwp-syncing');
        const originalText = button.getAttribute('data-original-text');
        if (originalText) {
          button.textContent = originalText;
        }
      }
    });

    // Block dynamic checkout buttons (PayPal, Shop Pay, etc.)
    const dynamicButtons = document.querySelectorAll(
      '.cart__dynamic-checkout-buttons, .additional-checkout-buttons'
    );
    dynamicButtons.forEach(container => {
      if (blocked) {
        container.style.pointerEvents = 'none';
        container.style.opacity = '0.5';
      } else {
        container.style.pointerEvents = '';
        container.style.opacity = '';
      }
    });
  }

  /**
   * Refresh cart UI after sync
   */
  function refreshCartUI() {
    // Trigger Shopify's pub/sub cart update
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'gwp-bundle',
        cartData: null
      });
    }

    // For cart drawer - re-fetch and render
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof routes !== 'undefined') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          const newDrawerContent = doc.querySelector('#CartDrawer');
          const currentDrawerContent = document.querySelector('#CartDrawer');

          if (newDrawerContent && currentDrawerContent) {
            currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
            // Re-bind overlay click
            const overlay = currentDrawerContent.querySelector('#CartDrawer-Overlay');
            if (overlay && cartDrawer.close) {
              overlay.addEventListener('click', () => cartDrawer.close());
            }
            initializeGWPLineItems();
          }
        })
        .catch(err => warn('Error refreshing cart drawer:', err));
    }

    // For cart page - trigger onCartUpdate if available
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      cartItems.onCartUpdate();
    }

    // Update cart icon bubble
    updateCartIconBubble();
  }

  /**
   * Update cart icon with current count
   */
  function updateCartIconBubble() {
    if (typeof routes === 'undefined') return;

    fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBubble = doc.querySelector('.shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');

        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      })
      .catch(err => warn('Error updating cart bubble:', err));
  }

  /**
   * Announce message to screen readers
   */
  function announceToScreenReader(message) {
    let announcer = document.getElementById('gwp-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'gwp-announcer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'visually-hidden';
      announcer.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
      document.body.appendChild(announcer);
    }
    // Clear and set to trigger announcement
    announcer.textContent = '';
    setTimeout(() => {
      announcer.textContent = message;
    }, 100);
  }

  // ============================================
  // LINE ITEM UI TREATMENT
  // ============================================

  /**
   * Initialize GWP-specific styling on line items
   */
  function initializeGWPLineItems() {
    // Find all GWP line items by data attribute or class
    const gwpItems = document.querySelectorAll('[data-gwp-item="true"], .cart-item--gwp');

    gwpItems.forEach(item => {
      // Ensure quantity controls are hidden
      const quantityWrapper = item.querySelector('.cart-item__quantity-wrapper, quantity-input, .quantity');
      if (quantityWrapper) {
        quantityWrapper.style.display = 'none';
      }

      // Ensure remove button is hidden
      const removeButton = item.querySelector('cart-remove-button, .cart-remove-button');
      if (removeButton) {
        removeButton.style.display = 'none';
      }

      // Make product links non-clickable
      const productLink = item.querySelector('.cart-item__name');
      if (productLink && productLink.tagName === 'A') {
        productLink.style.pointerEvents = 'none';
        productLink.style.cursor = 'default';
        productLink.removeAttribute('href');
      }
    });
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================

  const debouncedSync = debounce(syncGWPBundle, DEBOUNCE_DELAY);

  /**
   * Initialize all event listeners
   */
  function initializeEventListeners() {
    // Listen for cart updates via pub/sub
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Avoid infinite loop - don't sync on our own updates
        if (event.source === 'gwp-bundle') return;
        log('Cart update detected, scheduling sync...');
        debouncedSync();
      });
    }

    // Intercept checkout form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      const action = form.getAttribute('action') || '';
      const name = form.getAttribute('name') || '';

      // Check if this is a checkout form
      if (action.includes('/checkout') || name === 'checkout' || form.id === 'cart' || form.id === 'CartDrawer-Form') {
        const submitButton = form.querySelector('[name="checkout"]');
        if (submitButton && submitButton.hasAttribute('name')) {
          if (state.checkoutBlocked) {
            e.preventDefault();
            e.stopPropagation();
            announceToScreenReader('Please wait while your free gifts are being updated');
            return false;
          }
        }
      }
    }, true);

    // Block checkout button clicks during sync
    document.addEventListener('click', (e) => {
      const button = e.target.closest('#checkout, #CartDrawer-Checkout, [name="checkout"], .cart__checkout-button');
      if (button && state.checkoutBlocked) {
        e.preventDefault();
        e.stopPropagation();
        announceToScreenReader('Please wait while your free gifts are being updated');
        return false;
      }
    }, true);

    // Watch for DOM changes to initialize new GWP items
    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;

      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && (node.classList.contains('cart-item') || node.classList.contains('cart-item--gwp'))) {
                shouldInit = true;
              }
              if (node.querySelector && node.querySelector('.cart-item, .cart-item--gwp')) {
                shouldInit = true;
              }
            }
          });
        }
      });

      if (shouldInit) {
        initializeGWPLineItems();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Handle visibility change (user comes back to tab)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        log('Page became visible, checking cart sync...');
        debouncedSync();
      }
    });
  }

  // ============================================
  // CHECKOUT VALIDATION
  // ============================================

  /**
   * Validate cart is in sync before allowing checkout
   */
  async function validateBeforeCheckout() {
    const cart = await fetchCart();
    if (!cart) return false;

    const required = calculateRequiredGWPs(cart.items);
    const current = getCurrentGWPsInCart(cart.items);

    return validateSync(required, current);
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    log('Initializing GWP Bundle Manager');

    // Wait for required dependencies
    if (typeof routes === 'undefined') {
      warn('Waiting for routes to be defined...');
      setTimeout(init, 100);
      return;
    }

    // Initial sync on page load
    syncGWPBundle();

    // Set up event listeners
    initializeEventListeners();

    // Initialize line item styling
    initializeGWPLineItems();

    // Expose API for debugging
    window.GWPBundle = {
      sync: syncGWPBundle,
      validate: validateBeforeCheckout,
      getConfig: () => GWP_CONFIG,
      getState: () => ({ ...state }),
      getAllVariantIds: () => Array.from(ALL_GWP_VARIANT_IDS),
      isGWPVariant,
      isQualifyingProduct
    };

    log('GWP Bundle Manager initialized');
  }

  // Start initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Small delay to ensure other scripts have loaded
    setTimeout(init, 50);
  }

})();
